import { gql, useLazyQuery } from "@apollo/client";
import { useEffect } from "react";
import { useCourieStore } from "@src/common/lib/store";
import {
  PackageTypeCategory,
  PackageTypeDefaultWeightApplication,
} from "@api/graphql/generated/generated-types";

const PACKAGE_TYPES_FOR_ORDER = gql`
  query PackageTypesForOrder($courierId: ID!, $endCustomerId: ID) {
    packageTypes(courierId: $courierId, endCustomerId: $endCustomerId) {
      id
      courierId
      name
      enabled
      defaultLength
      defaultWidth
      defaultHeight
      defaultWeight
      defaultWeightApplication
      category
    }
  }
`;

export const PACKAGE_TYPES_LOAD_ERROR_MESSAGE =
  "Couldn't load package types. Please try again, or contact support if this keeps happening.";

export interface PackageTypeForOrder {
  id: string;
  courierId: string;
  name: string;
  enabled: boolean;
  defaultLength?: number | null;
  defaultWidth?: number | null;
  defaultHeight?: number | null;
  defaultWeight?: number | null;
  defaultWeightApplication?: PackageTypeDefaultWeightApplication | null;
  category: PackageTypeCategory;
}

interface CourierForPackageTypes {
  id: string;
  packageTypes?: PackageTypeForOrder[] | null;
}

interface PackageTypesForOrderData {
  packageTypes: PackageTypeForOrder[];
}

interface PackageTypesForOrderVariables {
  courierId: string;
  endCustomerId?: string | null;
}

/**
 * Loads package types for order entry/edit.
 * - No endCustomerId: reuse courier.packageTypes already on the session (no GraphQL).
 * - With endCustomerId: fetch with overrides applied; still drop enabled=false.
 * Falls back to undefined (no package-type picker) when empty. Empty and
 * not-known-yet are both undefined, so `arePackageTypesResolved` is what tells
 * them apart — anything that acts on absence must gate on it.
 * Failures toast here and return `error` so callers can mark the field invalid.
 */
export function usePackageTypesForOrder(
  courier: CourierForPackageTypes | undefined | null,
  endCustomerId: string | undefined | null,
) {
  const { showToast } = useCourieStore();
  // `no-cache`, not `network-only`: this list is one customer's *view* of types
  // the cache normalizes by id alone, so writing it would have it trade names
  // and default dims with the courier-scoped list under the same keys.
  const [getPackageTypes, { data, loading, error, called, variables }] =
    useLazyQuery<PackageTypesForOrderData, PackageTypesForOrderVariables>(
      PACKAGE_TYPES_FOR_ORDER,
      { fetchPolicy: "no-cache" },
    );

  useEffect(() => {
    if (!courier?.id || !endCustomerId) return;
    getPackageTypes({
      variables: {
        courierId: courier.id,
        endCustomerId,
      },
    }).catch(() => {
      showToast({
        message: PACKAGE_TYPES_LOAD_ERROR_MESSAGE,
        type: "error",
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courier?.id, endCustomerId]);

  if (!endCustomerId) {
    return {
      allPackageTypes: visiblePackageTypes(courier?.packageTypes),
      loading: false,
      arePackageTypesResolved: Boolean(courier),
      error: undefined,
    };
  }

  return {
    allPackageTypes: visiblePackageTypes(data?.packageTypes),
    loading,
    // `loading` is still false on the render that asks for a new customer's list,
    // because the query is only kicked off in the effect above. So it can't stand
    // in for "the list is known" — that needs a settled response whose variables
    // match the customer currently being asked about.
    arePackageTypesResolved:
      called &&
      !loading &&
      !error &&
      variables?.endCustomerId === endCustomerId,
    error,
  };
}

function visiblePackageTypes(
  packageTypes: PackageTypeForOrder[] | null | undefined,
): PackageTypeForOrder[] | undefined {
  const visible = packageTypes?.filter(
    (packageType) => packageType.enabled !== false,
  );
  return visible && visible.length > 0 ? visible : undefined;
}
